import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackHome } from '@/lib/homeAnalytics';

const EXAMPLES = [
  'Create videos for my business',
  'Build a website without coding',
  'Automate repetitive tasks',
  'Write better marketing content',
];

export function HomeHero() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  // Navigation only — the /search page issues the single AI Search request.
  const go = (q: string) => navigate(`/search?q=${encodeURIComponent(q)}&ai=true`);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    trackHome('search_started');
    go(q);
  };

  return (
    <section className="border-b border-border/60">
      <div className="container mx-auto px-4 pt-10 pb-10 md:pt-16 md:pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-display text-4xl sm:text-5xl md:text-[3.5rem] font-bold tracking-tight leading-[1.05] mb-4">
            Discover AI tools that <span className="text-primary">actually fit your work.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Search AI tools by task, find alternatives to the ones you already use, and compare up to 4 side by side.
          </p>

          <form onSubmit={onSubmit} role="search" className="relative max-w-2xl mx-auto">
            <label htmlFor="home-goal" className="sr-only">What do you want to accomplish?</label>
            <div className="relative">
            <Sparkles className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-primary pointer-events-none" aria-hidden />
            <input
              id="home-goal"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What do you want to accomplish?"
              maxLength={300}
              className="w-full h-16 rounded-2xl border border-border bg-card pl-14 pr-4 sm:pr-44 text-base md:text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/60"
            />
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={!query.trim()}
              className="mt-3 w-full h-12 sm:mt-0 sm:w-auto sm:absolute sm:right-2 sm:top-2 rounded-xl"
            >
              Find My Tools <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>

          <p className="mt-4 text-sm text-muted-foreground">
            Start free. No account required to explore.
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={ex}
                type="button"
                onClick={() => {
                  trackHome('example_prompt_clicked', `example_${i + 1}`);
                  go(ex);
                }}
                className="min-h-[40px] px-4 py-2 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
